import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import {
  getMediaLibraryFolderTree,
  getMediaLibraryItemList,
} from '$/api/canvasRquest';
import type {
  MediaLibraryFolderTreeNode,
  MediaLibraryItem,
} from '$/api/server.types';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { importPersonalAssetsToTeam } from '@/domains/team/teamService';
import styles from './index.module.scss';

const PERSONAL_ASSET_IMPORT_PAGE_SIZE = 100;

type PersonalAssetFolderNode = Omit<MediaLibraryFolderTreeNode, 'children'> & {
  children: PersonalAssetFolderNode[];
};

type PersonalAssetImportRow =
  | { kind: 'folder'; folder: PersonalAssetFolderNode; depth: number; }
  | { kind: 'item'; item: MediaLibraryItem; depth: number; };

interface PersonalAssetImportDialogProps {
  visible: boolean;
  teamOid: number;
  targetFolderId: number;
  onClose: () => void;
  onImported: () => Promise<void>;
}

interface ImportSelectionCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
}

/** 将接口可能返回的嵌套或扁平目录统一整理为稳定的树结构。 */
function normalizePersonalAssetFolderTree(
  folders: MediaLibraryFolderTreeNode[],
  unnamedFolderText: string,
): PersonalAssetFolderNode[] {
  const folderById = new Map<string, PersonalAssetFolderNode>();
  const pending = [...folders].reverse();

  while (pending.length > 0) {
    const folder = pending.pop();
    if (!folder) break;
    const folderKey = String(folder.id);
    if (!folderById.has(folderKey)) {
      folderById.set(folderKey, {
        ...folder,
        name: folder.name || unnamedFolderText,
        children: [],
      });
    }
    if (folder.children?.length) {
      pending.push(...[...folder.children].reverse());
    }
  }

  const roots: PersonalAssetFolderNode[] = [];
  folderById.forEach((folder) => {
    const parent = folderById.get(String(folder.parent_id));
    if (Number(folder.parent_id) > 0 && parent) {
      parent.children.push(folder);
    } else {
      roots.push(folder);
    }
  });

  const folderStack = [...roots];
  while (folderStack.length > 0) {
    const folder = folderStack.pop();
    if (!folder) break;
    folder.children.sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
    folderStack.push(...folder.children);
  }
  roots.sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
  return roots;
}

/** 拉取个人作用域下的全部素材，分页读取避免只展示第一页。 */
async function loadAllPersonalAssetItems(): Promise<MediaLibraryItem[]> {
  const items: MediaLibraryItem[] = [];
  let page = 1;
  let total: number | undefined;

  while (page <= 1000) {
    // 个人素材列表必须逐页读取，后页依赖前页返回的 total。
    // eslint-disable-next-line no-await-in-loop
    const response = await getMediaLibraryItemList(
      { folder_id: 0, page, page_size: PERSONAL_ASSET_IMPORT_PAGE_SIZE },
      { scope: 'personal' },
    );
    const rows = Array.isArray(response.list) ? response.list : [];
    items.push(...rows);
    const responseTotal = Number(response.total);
    if (Number.isFinite(responseTotal)) total = responseTotal;
    if (rows.length === 0 || (total != null && items.length >= total)) break;
    if (total == null && rows.length < PERSONAL_ASSET_IMPORT_PAGE_SIZE) break;
    page += 1;
  }

  return Array.from(new Map(items.map((item) => [String(item.id), item])).values());
}

/** 将素材按所属文件夹分组，根目录素材使用 key "0"。 */
function groupPersonalAssetsByFolder(items: MediaLibraryItem[]): Map<string, MediaLibraryItem[]> {
  const itemsByFolderId = new Map<string, MediaLibraryItem[]>();
  items.forEach((item) => {
    const folderKey = String(item.folder_id ?? 0);
    const folderItems = itemsByFolderId.get(folderKey) ?? [];
    folderItems.push(item);
    itemsByFolderId.set(folderKey, folderItems);
  });
  return itemsByFolderId;
}

/** 递归收集当前文件夹及所有子文件夹内的素材 ID。 */
function collectPersonalFolderAssetIds(
  folder: PersonalAssetFolderNode,
  itemsByFolderId: Map<string, MediaLibraryItem[]>,
  result: Map<string, string[]>,
): string[] {
  const folderKey = String(folder.id);
  const assetIds = (itemsByFolderId.get(folderKey) ?? []).map((item) => String(item.id));
  folder.children.forEach((child) => {
    assetIds.push(...collectPersonalFolderAssetIds(child, itemsByFolderId, result));
  });
  result.set(folderKey, assetIds);
  return assetIds;
}

/** 为每个文件夹建立包含后代素材的选择范围。 */
function buildPersonalFolderAssetIdMap(
  folders: PersonalAssetFolderNode[],
  itemsByFolderId: Map<string, MediaLibraryItem[]>,
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  folders.forEach((folder) => collectPersonalFolderAssetIds(folder, itemsByFolderId, result));
  return result;
}

/** 根据展开状态生成当前可见的目录与素材行，目录始终排在同层素材之前。 */
function buildVisiblePersonalAssetRows(
  folders: PersonalAssetFolderNode[],
  itemsByFolderId: Map<string, MediaLibraryItem[]>,
  expandedFolderIds: Set<string>,
): PersonalAssetImportRow[] {
  const rows: PersonalAssetImportRow[] = [];
  const pending: PersonalAssetImportRow[] = [];
  const rootItems = itemsByFolderId.get('0') ?? [];

  pending.push(...rootItems.slice().reverse().map((item) => ({ kind: 'item' as const, item, depth: 0 })));
  pending.push(...folders.slice().reverse().map((folder) => ({ kind: 'folder' as const, folder, depth: 0 })));

  while (pending.length > 0) {
    const row = pending.pop();
    if (!row) break;
    rows.push(row);
    if (row.kind === 'folder' && expandedFolderIds.has(String(row.folder.id))) {
      const childDepth = row.depth + 1;
      const folderItems = itemsByFolderId.get(String(row.folder.id)) ?? [];
      pending.push(...folderItems.slice().reverse().map((item) => ({ kind: 'item' as const, item, depth: childDepth })));
      pending.push(...row.folder.children.slice().reverse().map((folder) => ({
        kind: 'folder' as const,
        folder,
        depth: childDepth,
      })));
    }
  }
  return rows;
}

/** 获取树中全部文件夹 ID，用于弹窗首次打开时展开完整个人目录。 */
function getAllPersonalFolderIds(folders: PersonalAssetFolderNode[]): Set<string> {
  const result = new Set<string>();
  const pending = [...folders];
  while (pending.length > 0) {
    const folder = pending.pop();
    if (!folder) break;
    result.add(String(folder.id));
    pending.push(...folder.children);
  }
  return result;
}

/** 计算一组素材在当前选择集中的全选与半选状态。 */
function getPersonalAssetSelectionState(
  assetIds: string[],
  selectedIds: Set<string>,
): { checked: boolean; indeterminate: boolean; } {
  const selectedCount = assetIds.reduce((count, id) => count + (selectedIds.has(id) ? 1 : 0), 0);
  return {
    checked: assetIds.length > 0 && selectedCount === assetIds.length,
    indeterminate: selectedCount > 0 && selectedCount < assetIds.length,
  };
}

/** 返回素材行的缩略图地址，视频优先使用服务端封面。 */
function getPersonalAssetPreviewUrl(item: MediaLibraryItem): string {
  const thumbnailUrl = item.thumbnail_url?.trim() ?? '';
  if (thumbnailUrl) return thumbnailUrl;
  return item.material_type === 'image' ? item.url?.trim() ?? '' : '';
}

/** 为缺少缩略图的素材选择可用的本地图标。 */
function getPersonalAssetFallbackIcon(item: MediaLibraryItem): string {
  if (item.material_type === 'audio') return 'musicV3Bold';
  if (item.material_type === 'video') return 'videoV4Bold';
  if (item.material_type === 'script') return 'tableBold';
  if (item.material_type === 'image') return 'pictureV2Bold';
  return 'fileBold';
}

/** 渲染与设计稿一致的选中、半选和未选复选框。 */
function ImportSelectionCheckbox({
  checked,
  indeterminate = false,
}: ImportSelectionCheckboxProps): React.ReactElement {
  const active = checked || indeterminate;
  return (
    <span
      className={cn(styles.personalAssetImportCheckbox, {
        [styles.personalAssetImportCheckboxActive]: active,
      })}
      aria-hidden
    >
      {indeterminate && <IconFont name="minusFill" fontSize={10} color="#fff" />}
      {checked && <IconFont name="checkBoxMark" fontSize={10} color="#fff" />}
    </span>
  );
}

/** 渲染个人素材缩略图；地址不可用时保留素材类型图标。 */
function PersonalAssetImportThumbnail({ item }: { item: MediaLibraryItem; }): React.ReactElement {
  const previewUrl = getPersonalAssetPreviewUrl(item);
  return (
    <span className={styles.personalAssetImportThumbnail} aria-hidden>
      <IconFont name={getPersonalAssetFallbackIcon(item)} fontSize={16} color="currentColor" />
      {previewUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={previewUrl}
          alt=""
          crossOrigin="anonymous"
          onError={(event) => {
            event.currentTarget.remove();
          }}
        />
      )}
    </span>
  );
}

/** 团队资产库的“从个人资产导入”选择弹窗。 */
export default function PersonalAssetImportDialog({
  visible,
  teamOid,
  targetFolderId,
  onClose,
  onImported,
}: PersonalAssetImportDialogProps): React.ReactElement | null {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const closeText = languageStore.smartcatTranslationText((value) => value.close);
  const cancelText = languageStore.smartcatTranslationText(
    (value) => value.RoboNeo_Web_toast_cancel,
  );
  const confirmText = languageStore.smartcatTranslationText(
    (value) => value.RoboNeo_Web_toast_confirm,
  );
  const loadingText = languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_loading);
  const loadFailedText = languageStore.smartcatTranslationText(
    (value) => value.roboneo_web_loading_failed,
  );
  const retryText = languageStore.smartcatTranslationText((value) => value.roboneo_web_retry);
  const importFromPersonalAssetsText = languageStore.smartcatTranslationText(
    (value) => value.import_from_personal_assets,
  );
  const selectAllText = languageStore.smartcatTranslationText(
    (value) => value.select_all_num_selected_,
  );
  const emptyText = languageStore.smartcatTranslationText(
    (value) => value.no_personal_assets_available_for_import_,
  );
  const importSuccessText = languageStore.smartcatTranslationText(
    (value) => value['Successfully_imported_{num}_assets'],
  );
  const importOverwriteText = languageStore.smartcatTranslationText(
    (value) => value.Overwrote_num_existing_records,
  );
  const importSuccessWithOverwriteText = languageStore.smartcatTranslationText(
    (value) => value.Successfully_imported_num_assets_overwriting_num_existing_records,
  );
  const importFailedText = languageStore.smartcatTranslationText(
    (value) => value.failed_to_import_from_personal_assets_please_try_again_later_,
  );
  const unnamedFolderText = languageStore.smartcatTranslationText(
    (value) => value.unnamed_folder,
  );
  const [folders, setFolders] = useState<PersonalAssetFolderNode[]>([]);
  const [items, setItems] = useState<MediaLibraryItem[]>([]);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() => new Set());
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    const expectedTeamOid = teamOid;
    setLoading(true);
    setLoadFailed(false);
    setFolders([]);
    setItems([]);
    setSelectedItemIds(new Set());
    setSubmitting(false);

    /** 并行读取个人目录和全部个人素材，只写入仍属于当前团队弹窗的响应。 */
    async function loadPersonalAssetLibrary(): Promise<void> {
      try {
        const [folderResponse, personalItems] = await Promise.all([
          getMediaLibraryFolderTree({ scope: 'personal' }),
          loadAllPersonalAssetItems(),
        ]);
        if (!active || useOfficialStore.getState().clientOId !== expectedTeamOid) return;
        const normalizedFolders = normalizePersonalAssetFolderTree(
          folderResponse.list ?? [],
          unnamedFolderText,
        );
        setFolders(normalizedFolders);
        setItems(personalItems);
        setExpandedFolderIds(getAllPersonalFolderIds(normalizedFolders));
      } catch {
        if (active) setLoadFailed(true);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPersonalAssetLibrary().catch(() => {});
    return () => {
      active = false;
    };
  }, [reloadKey, teamOid, unnamedFolderText, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    /** Escape 关闭当前导入弹窗，提交过程中禁止打断请求。 */
    function handleImportDialogKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', handleImportDialogKeyDown);
    return () => document.removeEventListener('keydown', handleImportDialogKeyDown);
  }, [onClose, submitting, visible]);

  const itemsByFolderId = useMemo(() => groupPersonalAssetsByFolder(items), [items]);
  const folderAssetIdsByFolderId = useMemo(
    () => buildPersonalFolderAssetIdMap(folders, itemsByFolderId),
    [folders, itemsByFolderId],
  );
  const visibleRows = useMemo(
    () => buildVisiblePersonalAssetRows(folders, itemsByFolderId, expandedFolderIds),
    [expandedFolderIds, folders, itemsByFolderId],
  );
  const allItemIds = useMemo(() => items.map((item) => String(item.id)), [items]);
  const allSelectionState = useMemo(
    () => getPersonalAssetSelectionState(allItemIds, selectedItemIds),
    [allItemIds, selectedItemIds],
  );

  /** 切换一条素材或一个文件夹后代范围的选择状态。 */
  const toggleAssetSelection = useCallback((assetIds: string[]) => {
    if (assetIds.length === 0) return;
    setSelectedItemIds((previous) => {
      const next = new Set(previous);
      const allSelected = assetIds.every((id) => next.has(id));
      assetIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  }, []);

  /** 展开或收起一条个人资产文件夹。 */
  const toggleFolderExpanded = useCallback((folderId: string) => {
    setExpandedFolderIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  /** 全选或取消全选当前个人资产。 */
  const toggleSelectAll = useCallback(() => {
    setSelectedItemIds(allSelectionState.checked ? new Set() : new Set(allItemIds));
  }, [allItemIds, allSelectionState.checked]);

  /** 提交选中的个人资产到当前团队的指定文件夹，并刷新外层团队资产库。 */
  const handleImportSubmit = useCallback(async () => {
    if (selectedItemIds.size === 0 || submitting) return;
    if (useOfficialStore.getState().clientOId !== teamOid) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const result = await importPersonalAssetsToTeam(
        Array.from(selectedItemIds),
        targetFolderId,
      );
      if (useOfficialStore.getState().clientOId !== teamOid) return;
      await onImported();
      const importedText = importSuccessText.replace('{num}', String(result.count));
      const overwrittenText = importOverwriteText.replace('{num}', String(result.overwrite));
      if (result.overwrite > 0) {
        const successMessage = result.count > 0
          ? importSuccessWithOverwriteText
            .replace('{num}', String(result.count))
            .replace('{num}', String(result.overwrite))
          : overwrittenText;
        message.warning(successMessage);
      } else {
        message.success(importedText);
      }
      onClose();
    } catch (error) {
      if (useOfficialStore.getState().clientOId !== teamOid) return;
      const errorMessage = error instanceof Error && error.message
        ? error.message
        : importFailedText;
      message.error(errorMessage);
    } finally {
      if (useOfficialStore.getState().clientOId === teamOid) setSubmitting(false);
    }
  }, [
    importFailedText,
    importOverwriteText,
    importSuccessText,
    importSuccessWithOverwriteText,
    onClose,
    onImported,
    selectedItemIds,
    submitting,
    teamOid,
    targetFolderId,
  ]);

  if (!visible || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={styles.personalAssetImportOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="personal-asset-import-title"
    >
      <button
        type="button"
        className={styles.personalAssetImportBackdrop}
        aria-label={closeText}
        disabled={submitting}
        onClick={onClose}
        tabIndex={-1}
      />
      <div className={styles.personalAssetImportDialog}>
        <div className={styles.personalAssetImportHeader}>
          <h2 id="personal-asset-import-title">{importFromPersonalAssetsText}</h2>
          <button
            type="button"
            className={styles.personalAssetImportClose}
            aria-label={closeText}
            disabled={submitting}
            onClick={onClose}
          >
            <IconFont name="crossBlack" fontSize={20} color="currentColor" />
          </button>
        </div>

        <div className={styles.personalAssetImportList} role="tree" aria-busy={loading}>
          {loading && (
            <div className={styles.personalAssetImportState}>{loadingText}</div>
          )}
          {!loading && loadFailed && (
            <div className={styles.personalAssetImportState}>
              <span>{loadFailedText}</span>
              <button type="button" onClick={() => setReloadKey((value) => value + 1)}>
                {retryText}
              </button>
            </div>
          )}
          {!loading && !loadFailed && visibleRows.length === 0 && (
            <div className={styles.personalAssetImportState}>{emptyText}</div>
          )}
          {!loading && !loadFailed && visibleRows.map((row) => {
            if (row.kind === 'folder') {
              const folderKey = String(row.folder.id);
              const folderAssetIds = folderAssetIdsByFolderId.get(folderKey) ?? [];
              const selectionState = getPersonalAssetSelectionState(folderAssetIds, selectedItemIds);
              const disabled = folderAssetIds.length === 0;
              const expanded = expandedFolderIds.has(folderKey);
              const hasChildren = row.folder.children.length > 0
                || (itemsByFolderId.get(folderKey)?.length ?? 0) > 0;
              return (
                <div
                  key={`folder-${folderKey}`}
                  className={cn(styles.personalAssetImportRow, {
                    [styles.personalAssetImportRowSelected]: selectionState.checked || selectionState.indeterminate,
                    [styles.personalAssetImportRowDisabled]: disabled,
                  })}
                  style={{ paddingLeft: Math.min(8 + row.depth * 16, 120) }}
                  role="treeitem"
                  aria-level={row.depth + 1}
                  aria-expanded={hasChildren ? expanded : undefined}
                  aria-disabled={disabled || undefined}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      className={styles.personalAssetImportExpand}
                      onClick={() => toggleFolderExpanded(folderKey)}
                    >
                      <IconFont
                        name={expanded ? 'chevronDownBlack' : 'chevronRightBold'}
                        fontSize={8}
                        color="currentColor"
                      />
                    </button>
                  ) : (
                    <span className={styles.personalAssetImportExpandPlaceholder} aria-hidden />
                  )}
                  <button
                    type="button"
                    className={styles.personalAssetImportRowSelect}
                    role="checkbox"
                    aria-checked={selectionState.indeterminate ? 'mixed' : selectionState.checked}
                    disabled={disabled}
                    onClick={() => toggleAssetSelection(folderAssetIds)}
                  >
                    <IconFont
                      className={styles.personalAssetImportFolderIcon}
                      name="personalAssetImportFolder"
                      fontSize={32}
                      color="#F0F5FF"
                      aria-hidden
                    />
                    <span className={styles.personalAssetImportName}>
                      {row.folder.name}
                    </span>
                    {!disabled && (
                      <ImportSelectionCheckbox
                        checked={selectionState.checked}
                        indeterminate={selectionState.indeterminate}
                      />
                    )}
                  </button>
                </div>
              );
            }

            const itemId = String(row.item.id);
            const checked = selectedItemIds.has(itemId);
            return (
              <div
                key={`item-${itemId}`}
                className={cn(styles.personalAssetImportRow, {
                  [styles.personalAssetImportRowSelected]: checked,
                })}
                style={{ paddingLeft: Math.min(8 + row.depth * 16, 120) }}
                role="treeitem"
                aria-level={row.depth + 1}
              >
                <span className={styles.personalAssetImportExpandPlaceholder} aria-hidden />
                <button
                  type="button"
                  className={styles.personalAssetImportRowSelect}
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => toggleAssetSelection([itemId])}
                >
                  <PersonalAssetImportThumbnail item={row.item} />
                  <span className={styles.personalAssetImportName}>
                    {row.item.name}
                  </span>
                  <ImportSelectionCheckbox checked={checked} />
                </button>
              </div>
            );
          })}
        </div>

        <div className={styles.personalAssetImportFooter}>
          <button
            type="button"
            className={styles.personalAssetImportSelectAll}
            role="checkbox"
            aria-checked={allSelectionState.indeterminate ? 'mixed' : allSelectionState.checked}
            disabled={allItemIds.length === 0 || loading || loadFailed}
            onClick={toggleSelectAll}
          >
            <ImportSelectionCheckbox
              checked={allSelectionState.checked}
              indeterminate={allSelectionState.indeterminate}
            />
            <span>{selectAllText.replace('{num}', String(selectedItemIds.size))}</span>
          </button>
          <div className={styles.personalAssetImportActions}>
            <button
              type="button"
              className={styles.personalAssetImportCancel}
              disabled={submitting}
              onClick={onClose}
            >
              {cancelText}
            </button>
            <button
              type="button"
              className={styles.personalAssetImportSubmit}
              disabled={selectedItemIds.size === 0 || submitting}
              onClick={handleImportSubmit}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
